import React from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { useI18n } from "../../i18n";

const Return = () => {
  const { lang, t } = useI18n();
  return (
    <>
      <Header />
      
      <section className="pt-36 pb-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="rise font-display text-5xl md:text-6xl leading-[1.05] mb-4 text-ink">
            {lang === "en" ? "Return Policy" : t("footer.legal.return")}
          </h1>
          {lang !== "en" && (
            <p className="mb-4 rounded-2xl border border-line bg-surface px-4 py-3 text-sm text-ink-2">{t("legal.notice")}</p>
          )}
          <p className="text-ink-3 text-sm mb-12 pb-8 border-b border-line">
            Last Updated: December 2024
          </p>

          <div className="legal" dir="ltr" lang="en">
            {/* Important Notice */}
            <section>
              <div className="rounded-lg bg-brand-soft border border-brand/20 p-6 mb-8">
                <h2>Important Notice</h2>
                <p className="text-base leading-relaxed">
                  <strong>Optivra Tech provides digital services and solutions only.</strong> Since our offerings are intangible digital services (AI automation, software development, consulting), traditional product returns do not apply. However, this policy outlines our service-related return and exchange procedures.
                </p>
              </div>
            </section>

            {/* Overview */}
            <section>
              <h2>1. Return Policy Overview</h2>
              <p className="text-base leading-relaxed mb-4">
                This Return Policy describes how Optivra Tech handles requests for service cancellations, refunds, and adjustments for digital services purchased through our Platform (optivratech.com).
              </p>
              <p className="text-base leading-relaxed">
                <strong>Nature of Services:</strong> As a provider of digital IT services (software development, AI automation, consulting), we do not ship physical products. All deliverables are provided digitally via email, cloud storage, or online deployment.
              </p>
            </section>

            {/* Return Eligibility */}
            <section>
              <h2>2. Return & Exchange Eligibility</h2>
              
              <h3>2.1 Service Return Window</h3>
              <p className="text-base leading-relaxed mb-4">
                We offer refund/exchange within the first <strong>7 days</strong> from the date of your service purchase or project initiation, subject to the following conditions:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>No project work has been initiated or delivered</li>
                <li>No design mockups, code, or deliverables have been shared</li>
                <li>No consultation sessions have been conducted</li>
                <li>No third-party services have been purchased on your behalf</li>
              </ul>

              <h3>2.2 After 7 Days</h3>
              <p className="text-base leading-relaxed mb-4">
                If 7 days have passed since your purchase or if any work has been initiated, you will not be offered a full return or refund. However, you may be eligible for:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Partial refund based on work not yet completed</li>
                <li>Service credit for future projects</li>
                <li>Project scope adjustment</li>
              </ul>
            </section>

            {/* Return Conditions */}
            <section>
              <h2>3. Conditions for Return</h2>
              <p className="text-base leading-relaxed mb-4">
                In order to become eligible for a return or service adjustment:
              </p>
              <ul className="space-y-2 ml-4">
                <li>The service/project must be in unused/uninitiated state</li>
                <li>No deliverables (code, designs, documents) have been provided to you</li>
                <li>Request must be submitted in writing within the eligible timeframe</li>
                <li>Any provided credentials or access must be maintained securely</li>
              </ul>
            </section>

            {/* Digital Services Exception */}
            <section>
              <h2>4. Digital Services - No Physical Returns</h2>
              <p className="text-base leading-relaxed mb-4">
                <strong>Important:</strong> Optivra Tech does not ship physical goods or products. All our services are delivered digitally. Therefore:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>There are no physical items to return</li>
                <li>No shipping or courier services are involved</li>
                <li>All deliverables are provided via secure digital channels</li>
                <li>Traditional product return logistics do not apply</li>
              </ul>
              <p className="text-base leading-relaxed">
                This policy focuses on service cancellation and refund procedures rather than physical product returns.
              </p>
            </section>

            {/* Exempted Services */}
            <section>
              <h2>5. Services Exempted from Returns</h2>
              <p className="text-base leading-relaxed mb-4">
                You agree that certain categories of services are exempted from returns or refunds:
              </p>
              <ul className="space-y-2 ml-4">
                <li><strong>Completed Services:</strong> Projects that have been delivered and approved</li>
                <li><strong>Consultation Sessions:</strong> Once conducted, consulting hours are non-refundable</li>
                <li><strong>Custom Development:</strong> After milestone approval, that portion is non-refundable</li>
                <li><strong>Third-party Purchases:</strong> Domain names, licenses, subscriptions bought for you</li>
                <li><strong>Discounted Services:</strong> Services purchased during promotional sales may not be eligible</li>
              </ul>
            </section>

            {/* Defective or Unsatisfactory Services */}
            <section>
              <h2>6. Defective or Unsatisfactory Services</h2>
              
              <h3>6.1 Quality Issues</h3>
              <p className="text-base leading-relaxed mb-4">
                In case you believe the service delivered does not meet agreed specifications or quality standards:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Report to our customer service team within <strong>7 days</strong> of delivery</li>
                <li>Provide specific details about quality concerns</li>
                <li>Our team will review and determine corrective action</li>
                <li>We may offer revisions, rework, or partial refund as appropriate</li>
              </ul>

              <h3>6.2 Service Not as Described</h3>
              <p className="text-base leading-relaxed mb-4">
                If you feel that the service delivered is not as shown on the site or as per your expectations:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Bring it to our notice within <strong>7 days</strong> of receiving deliverables</li>
                <li>Provide comparison with original agreement or scope document</li>
                <li>Our customer service team will investigate and take appropriate decision</li>
                <li>We prioritize customer satisfaction and will work to resolve concerns</li>
              </ul>
            </section>

            {/* Return Process */}
            <section>
              <h2>7. Return Request Process</h2>
              
              <h3>Step 1: Submit Request</h3>
              <p className="text-base leading-relaxed mb-4">
                Send an email to <strong>founder@optivratech.com</strong> with:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Subject: "Service Return Request"</li>
                <li>Order/Invoice number</li>
                <li>Service name and purchase date</li>
                <li>Reason for return request</li>
                <li>Supporting documentation (if applicable)</li>
              </ul>

              <h3>Step 2: Review and Inspection</h3>
              <p className="text-base leading-relaxed mb-4">
                Once we receive your return request:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>We will acknowledge receipt within 24 hours</li>
                <li>Our team will review the request and project status</li>
                <li>We may request additional information or clarification</li>
                <li>Review typically takes 2-3 business days</li>
              </ul>

              <h3>Step 3: Decision Notification</h3>
              <p className="text-base leading-relaxed mb-4">
                You will receive an email notification about:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Approval or rejection of return request</li>
                <li>Refund amount (if applicable)</li>
                <li>Alternative solutions (service credit, rework, etc.)</li>
                <li>Timeline for processing</li>
              </ul>

              <h3>Step 4: Refund Processing</h3>
              <p className="text-base leading-relaxed">
                If your return is approved, refund will be processed in accordance with our Refund Policy. In case of any refunds approved by Optivra Tech, it will take <strong>7-14 business days</strong> for the refund to be processed to your original payment method.
              </p>
            </section>

            {/* Service Credits */}
            <section>
              <h2>8. Service Credits as Alternative</h2>
              <p className="text-base leading-relaxed mb-4">
                In some cases, we may offer service credits as an alternative to refunds:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Service credits can be used for future projects</li>
                <li>Credits remain valid for 12 months from issue date</li>
                <li>Cannot be combined with other promotional offers</li>
                <li>Non-transferable and non-refundable for cash</li>
              </ul>
            </section>

            {/* Warranty Services */}
            <section>
              <h2>9. Warranty and Support</h2>
              <p className="text-base leading-relaxed mb-4">
                Services that come with post-delivery support or warranty:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Bug fixes covered under warranty period (typically 30-60 days)</li>
                <li>Technical support as specified in service agreement</li>
                <li>Refer to your specific service agreement for warranty terms</li>
                <li>Warranty claims must be reported during the warranty period</li>
              </ul>
            </section>

            {/* Disputes */}
            <section>
              <h2>10. Dispute Resolution</h2>
              <p className="text-base leading-relaxed mb-4">
                If you disagree with our return decision:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Request escalation to senior management</li>
                <li>Provide additional supporting evidence</li>
                <li>We will conduct a second review</li>
                <li>Final decision will be communicated within 5 business days</li>
              </ul>
            </section>

            {/* Contact Information */}
            <section>
              <h2>11. Contact for Return Requests</h2>
              <p className="text-base leading-relaxed mb-2">
                For all return-related inquiries and requests:
              </p>
              <div className="rounded-lg bg-surface border border-line p-6 mt-4">
                <p className="text-base mb-2"><strong>Optivra Tech</strong></p>
                <p className="text-base mb-2">Registered Office: C-52, Amar Colony, Udasar Fanta, Jaipur Road, Bikaner, Rajasthan, India</p>
                <p className="text-base mb-2">GST Number: 08BNOPS7805R1ZT</p>
                <p className="text-base mb-2">Email: founder@optivratech.com</p>
                <p className="text-base mb-2">Phone: +91 86198 38718</p>
                <p className="text-base mb-2">Subject Line: "Service Return Request"</p>
                <p className="text-base">Business Hours: Monday - Saturday (9:00 AM - 6:00 PM IST)</p>
              </div>
            </section>

            {/* Policy Updates */}
            <section>
              <h2>12. Changes to Return Policy</h2>
              <p className="text-base leading-relaxed">
                We reserve the right to modify this Return Policy at any time. Changes will be effective immediately upon posting on our website with an updated "Last Updated" date. Continued use of our services after changes indicates acceptance of the modified policy.
              </p>
            </section>

            {/* Summary */}
            <section className="mt-8">
              <div className="rounded-lg bg-brand-soft border border-brand/20 p-6">
                <h3>Summary</h3>
                <ul className="space-y-2">
                  <li className="text-base leading-relaxed">✓ Returns accepted within 7 days if no work initiated</li>
                  <li className="text-base leading-relaxed">✓ Digital services only - no physical products</li>
                  <li className="text-base leading-relaxed">✓ Partial refunds based on work completion status</li>
                  <li className="text-base leading-relaxed">✓ Quality concerns addressed within 7 days of delivery</li>
                  <li className="text-base leading-relaxed">✓ Service credits available as alternative</li>
                  <li className="text-base leading-relaxed">✓ Refunds processed in 7-14 business days</li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Return;
